import { Typography } from '@mui/material';
import { Box } from '@mui/system';
import { StaffAppointmentAvailability } from 'common/dist/infrastructure/modules/appointment/interfaces/AvailabilityTypes';
import { format, isToday } from 'date-fns';
import { decode } from 'html-entities';
import React, { FC } from 'react';

import HorizontalCard, { HorizontalCardType } from 'infrastructure/components/HorizontalCard';
import HorizontalCardSkeleton from 'infrastructure/components/HorizontalCard/HorizontalCardSkeleton';
import {
  selectLocationDetails,
  selectReservationDate,
} from 'infrastructure/redux/slices/bookingWizard.selector';
import { useAppSelector } from 'infrastructure/redux/store/hooks';
import {
  formatPhoneNumber,
  formatToCurrency,
} from 'infrastructure/targets/web/modules/common/helpers';
import { getLessonBookingImage } from 'infrastructure/targets/web/modules/common/imageUrlHelpers';

interface IInstructorsList {
  instructorsList?: Array<StaffAppointmentAvailability>;
  loading?: boolean;
  onInstructorSelect?: (instructor: string) => void;
  onCardClick?: (value: number) => void;
}

const InstructorsList: FC<IInstructorsList> = ({ instructorsList, loading, onCardClick }) => {
  const reservationDate = useAppSelector(selectReservationDate);
  const selectedLocationDetails = useAppSelector(selectLocationDetails);
  const phoneNumber = selectedLocationDetails?.telephone;
  const handleCardClick = (value: number) => {
    if (onCardClick) {
      onCardClick(value);
    }
  };

  const getButtonTitle = (title: string) => {
    if (title) {
      if (/1 Hour/g.test(title) && !/Intro/g.test(title)) {
        return '1 Hour Lesson';
      }
      if (/1.5 Hour/g.test(title)) {
        return '1.5 Hour Lesson';
      }
      if (/0.5 Hour/g.test(title)) {
        return '0.5 Hour Lesson';
      }
      if (/.5 Hour/g.test(title)) {
        return '0.5 Hour Lesson';
      }
      if (/45 Minute/g.test(title)) {
        return '45 Minute Lesson';
      }
      if (/Group/g.test(title)) {
        return 'Group Lessons';
      }
      if (/Swing/g.test(title)) {
        return 'Swing Evaluation, 1 Hour';
      }
      return title;
    }
  };

  const getButtonList = (instructorId: number) => {
    const selectedInstructor = instructorsList?.find(
      (insctructor) => insctructor.staffId === instructorId,
    );
    const doesFittings = selectedInstructor?.services
      ?.filter((service) => /Fitting/g.test(service?.title))
      ?.sort((a, b) => a.price - b.price)[0];

    let mappedServices = selectedInstructor?.services
      ?.filter((service) => !/Intro/g.test(service?.title || ''))
      ?.filter((service) => !/Fitting/g.test(service?.title || ''))
      ?.sort((a, b) => a.defaultTimeLength - b.defaultTimeLength)
      ?.map((service) => ({
        title: getButtonTitle(service.title),
        value: service.sessionTypeId,
        label: !/Group/g.test(service?.title || '') ? '1 Person' : '2-3 People',
        subtitle: formatToCurrency(service.price),
        highlight: /Swing/g.test(service?.title) ? true : false,
      }));
    if (doesFittings) {
      mappedServices = [
        ...(mappedServices ?? []),
        {
          title: 'Club Fitting',
          value: doesFittings.sessionTypeId,
          label: 'Starting At',
          subtitle: formatToCurrency(doesFittings.price),
          highlight: false,
        },
      ];
    }
    return mappedServices?.sort((a, b) => {
      if (a.highlight === b.highlight) {
        return 0;
      }
      return a.highlight ? -1 : 1;
    });
  };

  const getServicesList = (instructorId: number) => {
    const serviceList = [];
    const selectedInstructor = instructorsList?.find(
      (insctructor) => insctructor.staffId === instructorId,
    );

    const doesLessons = selectedInstructor?.services?.some((service) =>
      /Lesson/g.test(service?.title),
    );
    if (doesLessons) serviceList.push('Lessons');
    const doesSwingEvals = selectedInstructor?.services?.some((service) =>
      /Swing/g.test(service?.title),
    );
    if (doesSwingEvals) serviceList.push('Swing Evaluations');
    const doesFittings = selectedInstructor?.services?.some((service) =>
      /Fitting/g.test(service?.title),
    );
    if (doesFittings) serviceList.push('Club Fitting');

    return serviceList.map(String).join(', ');
  };

  return (
    <>
      {!loading && !instructorsList?.length && (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center' }}>
          <Typography variant="h5" textAlign="center">
            {isToday(new Date(reservationDate ?? '0'))
              ? `Same-day bookings are not available online. Call ${formatPhoneNumber(
                  phoneNumber,
                )} to book a same day lesson or to get more information. Please use the date selector for all future date availability.`
              : `Sorry, but there are no instructors available`}
          </Typography>
        </Box>
      )}
      {loading ? (
        <Box sx={{ width: '100%', display: 'flex', gap: 4, flexDirection: 'column' }}>
          <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center' }}>
            <Typography variant="h6">Fetching available instructors...</Typography>
          </Box>
          <HorizontalCardSkeleton />
          <HorizontalCardSkeleton />
        </Box>
      ) : (
        instructorsList?.map((instructor) => {
          const lastBooked = instructor.lastBookedAt
            ? format(new Date(instructor.lastBookedAt), 'd MMM yyyy')
            : undefined;
          return (
            <HorizontalCard
              key={instructor.staffId}
              buttonList={getButtonList(instructor?.staffId)}
              variant={HorizontalCardType.Large}
              chipLabel={instructor.staffLevel}
              title={`${instructor.staffFirstName} ${instructor.staffLastName}`}
              handleCTA={() => handleCardClick(instructor.staffId)}
              content={decode(instructor?.staffBio)}
              imageUrl={instructor.staffImage ? instructor.staffImage : getLessonBookingImage()}
              noBottomMargin
              ctaButtonTop
              caption={lastBooked ? `Last Booked ${lastBooked}` : undefined}
              services={getServicesList(instructor?.staffId)}
              label="Services"
              infoCardVariant={true}
            />
          );
        })
      )}
    </>
  );
};

export default InstructorsList;
